import { activeReconciliationForEvidence, type CalculationSnapshot, type EvidenceItem,
  type InvoiceDraft, type ReconciliationItem, type ResponsibilityStep } from '../shared';
import type { ClientHandoff, PracticeClientSummary, PracticeWorkItem } from './types';

/** The least data Home needs to project resumable work from the current client
 * workspace. Deleted/superseded records remain outside this projection. */
export interface PracticeHomeClientState {
  evidence: readonly EvidenceItem[];
  reconciliation: readonly ReconciliationItem[];
  calculation: CalculationSnapshot;
  responsibilities: readonly ResponsibilityStep[];
  invoices: readonly InvoiceDraft[];
  handoffs: readonly ClientHandoff[];
  taxReturn?: { id: string; status: string };
  pitReturn?: { id: string; status: string };
  pitCalculationView?: CalculationSnapshot;
  citCalculation?: { id: string };
}

/** Builds Home from live browser state instead of presentation fixtures. The
 * result is recalculated after every workspace mutation, including deletion. */
export function derivePracticeWorkItems(clients: readonly PracticeClientSummary[],
  workspaces: Readonly<Record<string, PracticeHomeClientState | undefined>>): PracticeWorkItem[] {
  return clients.flatMap((client) => workForClient(client, workspaces[client.id]));
}

function workForClient(client: PracticeClientSummary, state?: PracticeHomeClientState): PracticeWorkItem[] {
  if (!state) return [];
  const work: PracticeWorkItem[] = [];
  const activeEvidence = state.evidence.filter((item) => item.state !== 'unlinked' && item.state !== 'superseded');
  const activeReconciliation = activeReconciliationForEvidence(state.evidence, state.reconciliation);
  const blockedProposals = activeReconciliation.filter((item) => item.state === 'blocked');
  const reviewProposals = activeReconciliation.filter((item) => item.state === 'needs-review');
  const blockedRecords = activeEvidence.filter((item) => ['quarantined', 'processing-failed', 'blocked']
    .includes(item.state) || item.ownershipState === 'mismatch');
  const delayedRecords = activeEvidence.filter((item) => item.state === 'processing-delayed');
  const reviewRecords = activeEvidence.filter((item) => item.state === 'ready-for-review');
  const checkingRecords = activeEvidence.filter((item) => item.state === 'checking');

  if (blockedProposals.length || blockedRecords.length) {
    const count = blockedProposals.length + blockedRecords.length;
    work.push(item(client, 'records', 'Resolve blocked records',
      `${count} ${plural(count, 'record issue')} ${count === 1 ? 'needs' : 'need'} your review`, 'blocking'));
  } else if (reviewProposals.length || reviewRecords.length) {
    const count = reviewProposals.length || reviewRecords.length;
    work.push(item(client, 'records', 'Review record proposals',
      `${count} ${plural(count, 'record item')} ${count === 1 ? 'is' : 'are'} ready for review`, 'attention'));
  } else if (delayedRecords.length) {
    const count = delayedRecords.length;
    work.push(item(client, 'records', 'Check delayed record processing',
      `${count} ${plural(count, 'record')} ${count === 1 ? 'needs' : 'need'} a processing check`, 'attention'));
  } else if (checkingRecords.length) {
    const count = checkingRecords.length;
    work.push(item(client, 'records', 'Check record processing',
      `${count} ${plural(count, 'record')} still ${count === 1 ? 'has' : 'have'} processing in progress`, 'normal'));
  }

  const taxReturn = state.pitReturn ?? state.taxReturn;
  const hasCalculation = state.calculation.lines.length > 0 || Boolean(state.pitCalculationView?.lines.length)
    || Boolean(state.citCalculation);
  if ((taxReturn || hasCalculation) && !returnWorkComplete(taxReturn?.status)) {
    const blocked = taxReturn?.status === 'CORRECTION_REQUIRED' || taxReturn?.status === 'DISPATCH_AMBIGUOUS';
    work.push(item(client, 'return', blocked ? 'Resolve the return issue' : 'Continue the tax return',
      blocked ? 'Review the recorded return issue before continuing'
        : taxReturn ? returnNextAction(taxReturn.status) : 'Review the calculation and prepare the return',
      blocked ? 'blocking' : 'normal'));
  }

  const invoice = state.invoices.find((candidate) => candidate.state !== 'bridge-mock-received');
  if (invoice) {
    const blocked = invoice.state === 'blocked';
    const needsReview = invoice.state === 'needs-review';
    work.push(item(client, 'invoice', blocked ? 'Resolve the invoice issue' : 'Continue the e-invoice',
      invoice.issue || (needsReview ? 'Review the prepared invoice details' : 'Continue the current invoice draft'),
      blocked ? 'blocking' : needsReview ? 'attention' : 'normal'));
  }

  const handoff = state.handoffs.find((candidate) => candidate.state !== 'complete');
  if (handoff) {
    const expired = handoff.state === 'expired';
    work.push(item(client, 'handoff', expired ? 'Renew the client handoff' : 'Continue the client handoff',
      expired ? 'The client handoff expired before it was completed' : handoff.explanation,
      expired ? 'attention' : 'normal', handoff.recipient));
  }
  return work;
}

function item(client: PracticeClientSummary, kind: 'records' | 'return' | 'invoice' | 'handoff', title: string,
  nextAction: string, priority: PracticeWorkItem['priority'], owner = 'Current authorised practitioner'): PracticeWorkItem {
  const destination = kind === 'records' ? 'evidence' : kind === 'return' ? 'returns'
    : kind === 'invoice' ? 'invoices' : 'handoffs';
  return { id: `current:${client.id}:${kind}`, clientId: client.id, clientName: client.name, title, owner,
    dueLabel: '', priority, nextAction, destination };
}

function plural(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`;
}

function returnWorkComplete(status?: string): boolean {
  return status === 'MOCK_ACCEPTED';
}

function returnNextAction(status: string): string {
  if (status === 'DRAFT') return 'Prepare the return from the reviewed calculation';
  if (status === 'PREPARED') return 'Arrange independent professional review';
  if (status === 'REVIEWED') return 'Arrange approval by an authorised practitioner';
  if (status === 'APPROVED') return 'Send the approved return to the client for signature';
  if (status === 'SIGNED') return 'Review the filing step with an authorised submitter';
  if (status === 'SUBMISSION_REQUESTED') return 'Check the current delivery status';
  return 'Review the current return state and next authorised step';
}
